import { useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { useVolumeBuilder3d } from "./VolumeBuilder3dContext";
import {
    buildHandleGroup,
    buildPreviewGroup,
    compilePaths,
    packObjects,
} from "../../utils/volumeBuilderGeometry";
import type { HandleUserData, Vec3 } from "../../utils/volumeBuilderGeometry";

const HANDLE_RADIUS = 0.12;

export function VolumeBuilder3dViewer() {
    const {
        paths,
        packingBounds,
        cameraParams,
        viewMode,
        setViewMode,
        generatedSvg,
        isGenerating,
        generateProgress,
        error,
        generate,
        canExport,
        handleDownload,
        cameraRef,
        updatePoint,
        pushPathsSnapshot,
    } = useVolumeBuilder3d();

    const mountRef = useRef<HTMLDivElement>(null);
    const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
    const sceneRef = useRef<THREE.Scene | null>(null);
    const controlsRef = useRef<OrbitControls | null>(null);
    const animFrameRef = useRef<number>(0);
    const modelGroupRef = useRef<THREE.Group | null>(null);
    const handleGroupRef = useRef<THREE.Group | null>(null);

    // Latest callbacks for the pointer handlers (bound once in the mount effect).
    const updatePointRef = useRef(updatePoint);
    updatePointRef.current = updatePoint;
    const pushSnapshotRef = useRef(pushPathsSnapshot);
    pushSnapshotRef.current = pushPathsSnapshot;

    // Mount: renderer, scene, camera, controls, animation loop.
    useEffect(() => {
        const mount = mountRef.current;
        if (!mount) return;

        const width = Math.max(mount.clientWidth, 1);
        const height = Math.max(mount.clientHeight, 1);

        const renderer = new THREE.WebGLRenderer({ antialias: true });
        renderer.setPixelRatio(window.devicePixelRatio);
        renderer.setSize(width, height);
        renderer.setClearColor(0x1e1e2e, 1);
        mount.appendChild(renderer.domElement);
        rendererRef.current = renderer;

        const scene = new THREE.Scene();
        sceneRef.current = scene;

        const ambient = new THREE.AmbientLight(0xffffff, 0.6);
        scene.add(ambient);
        const dirLight = new THREE.DirectionalLight(0xffffff, 0.9);
        dirLight.position.set(5, 10, 7);
        scene.add(dirLight);
        scene.add(new THREE.GridHelper(12, 12, 0x394, 0x2a2a3a));

        const camera = new THREE.PerspectiveCamera(45, width / height, 0.01, 1000);
        camera.position.set(0, 0, 7);
        cameraRef.current = camera;

        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        controls.dampingFactor = 0.05;
        controlsRef.current = controls;

        // --- Drag handles: raycast against the handle overlay, drag on a
        // camera-facing plane through the grabbed point. ---
        const raycaster = new THREE.Raycaster();
        const pointer = new THREE.Vector2();
        const dragPlane = new THREE.Plane();
        const dragPoint = new THREE.Vector3();
        let dragging: HandleUserData | null = null;

        const updatePointerFromEvent = (event: PointerEvent) => {
            const rect = renderer.domElement.getBoundingClientRect();
            pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
            pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
        };

        const onPointerDown = (event: PointerEvent) => {
            if (event.button !== 0) return;
            const cam = cameraRef.current;
            const handles = handleGroupRef.current;
            if (!cam || !handles) return;
            updatePointerFromEvent(event);
            raycaster.setFromCamera(pointer, cam);
            const hits = raycaster.intersectObjects(handles.children, false);
            const hit = hits[0];
            if (!hit) return;
            const data = hit.object.userData as HandleUserData;
            if (data?.handlePathId == null) return;
            dragging = data;
            controls.enabled = false;
            pushSnapshotRef.current();
            renderer.domElement.setPointerCapture(event.pointerId);
        };

        const onPointerMove = (event: PointerEvent) => {
            if (!dragging) return;
            const cam = cameraRef.current;
            const handles = handleGroupRef.current;
            if (!cam || !handles) return;
            const handleMesh = handles.children.find((c) => {
                const d = c.userData as HandleUserData;
                return (
                    d.handlePathId === dragging!.handlePathId &&
                    d.handlePointIndex === dragging!.handlePointIndex
                );
            });
            if (!handleMesh) return;
            // Plane through the handle, facing the camera.
            const camDir = new THREE.Vector3();
            cam.getWorldDirection(camDir);
            dragPlane.setFromNormalAndCoplanarPoint(
                camDir,
                handleMesh.position,
            );
            updatePointerFromEvent(event);
            raycaster.setFromCamera(pointer, cam);
            if (!raycaster.ray.intersectPlane(dragPlane, dragPoint)) return;
            handleMesh.position.copy(dragPoint);
            const next: Vec3 = {
                x: dragPoint.x,
                y: dragPoint.y,
                z: dragPoint.z,
            };
            updatePointRef.current(
                dragging.handlePathId,
                dragging.handlePointIndex,
                next,
            );
        };

        const onPointerUp = (event: PointerEvent) => {
            if (!dragging) return;
            dragging = null;
            controls.enabled = true;
            try {
                renderer.domElement.releasePointerCapture(event.pointerId);
            } catch {
                /* no-op */
            }
        };

        renderer.domElement.addEventListener("pointerdown", onPointerDown);
        renderer.domElement.addEventListener("pointermove", onPointerMove);
        renderer.domElement.addEventListener("pointerup", onPointerUp);
        renderer.domElement.addEventListener("pointercancel", onPointerUp);

        const animate = () => {
            animFrameRef.current = requestAnimationFrame(animate);
            controls.update();
            const cam = cameraRef.current;
            if (!cam) return;
            // Keep billboards facing the camera.
            const group = modelGroupRef.current;
            if (group) {
                group.traverse((obj) => {
                    if (obj.userData?.billboard) {
                        obj.quaternion.copy(cam.quaternion);
                    }
                });
            }
            renderer.render(scene, cam);
        };
        animate();

        const ro = new ResizeObserver(() => {
            const w = Math.max(mount.clientWidth, 1);
            const h = Math.max(mount.clientHeight, 1);
            renderer.setSize(w, h);
            const cam = cameraRef.current;
            if (cam instanceof THREE.PerspectiveCamera) {
                cam.aspect = w / h;
                cam.updateProjectionMatrix();
            } else if (cam instanceof THREE.OrthographicCamera) {
                const aspect = w / h;
                const frust = cam.top;
                cam.left = -frust * aspect;
                cam.right = frust * aspect;
                cam.updateProjectionMatrix();
            }
        });
        ro.observe(mount);

        return () => {
            cancelAnimationFrame(animFrameRef.current);
            ro.disconnect();
            renderer.domElement.removeEventListener(
                "pointerdown",
                onPointerDown,
            );
            renderer.domElement.removeEventListener(
                "pointermove",
                onPointerMove,
            );
            renderer.domElement.removeEventListener("pointerup", onPointerUp);
            renderer.domElement.removeEventListener(
                "pointercancel",
                onPointerUp,
            );
            controls.dispose();
            renderer.dispose();
            if (mount.contains(renderer.domElement)) {
                mount.removeChild(renderer.domElement);
            }
            rendererRef.current = null;
            sceneRef.current = null;
            controlsRef.current = null;
            cameraRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Rebuild the preview group when the authored scene changes.
    const previewObjects = useMemo(
        () => packObjects(compilePaths(paths), paths, packingBounds),
        [paths, packingBounds],
    );

    useEffect(() => {
        const scene = sceneRef.current;
        if (!scene) return;

        const prev = modelGroupRef.current;
        if (prev) {
            scene.remove(prev);
            prev.traverse((obj) => {
                if (obj instanceof THREE.Mesh) {
                    obj.geometry.dispose();
                    const mat = obj.material;
                    if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
                    else mat.dispose();
                }
            });
            modelGroupRef.current = null;
        }

        const group = buildPreviewGroup(previewObjects);
        scene.add(group);
        modelGroupRef.current = group;
    }, [previewObjects]);

    // Rebuild draggable point handles when the authored paths change.
    useEffect(() => {
        const scene = sceneRef.current;
        if (!scene) return;

        const prev = handleGroupRef.current;
        if (prev) {
            scene.remove(prev);
            prev.traverse((obj) => {
                if (obj instanceof THREE.Mesh) {
                    obj.geometry.dispose();
                    const mat = obj.material;
                    if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
                    else mat.dispose();
                }
            });
            handleGroupRef.current = null;
        }

        const handles = buildHandleGroup(paths, HANDLE_RADIUS);
        scene.add(handles);
        handleGroupRef.current = handles;
    }, [paths]);

    // Sync camera params (perspective/ortho swap + distance) onto the live camera.
    useEffect(() => {
        const mount = mountRef.current;
        const controls = controlsRef.current;
        if (!mount || !rendererRef.current || !controls) return;

        const { fov, projectionType, distance } = cameraParams;
        const oldCamera = cameraRef.current;
        const target = controls.target.clone();

        const dir = new THREE.Vector3(0, 0, 1);
        if (oldCamera) {
            dir.subVectors(oldCamera.position, target);
            if (dir.lengthSq() > 1e-10) dir.normalize();
        }

        const w = Math.max(mount.clientWidth, 1);
        const h = Math.max(mount.clientHeight, 1);
        const aspect = w / h;
        let newCamera: THREE.PerspectiveCamera | THREE.OrthographicCamera;

        if (projectionType === "perspective") {
            const cam = new THREE.PerspectiveCamera(fov, aspect, 0.01, 1000);
            cam.position.copy(target).addScaledVector(dir, distance);
            cam.lookAt(target);
            newCamera = cam;
        } else {
            const frust = distance * 0.5;
            const cam = new THREE.OrthographicCamera(
                -frust * aspect,
                frust * aspect,
                frust,
                -frust,
                0.01,
                1000,
            );
            cam.position.copy(target).addScaledVector(dir, distance);
            cam.lookAt(target);
            newCamera = cam;
        }

        cameraRef.current = newCamera;
        controls.object = newCamera;
        controls.target.copy(target);
        controls.update();
    }, [cameraParams, cameraRef]);

    const progressText = useMemo(() => {
        if (!isGenerating) return null;
        if (!generateProgress || generateProgress.total <= 0) {
            return "Projecting…";
        }
        const pct = Math.round(
            (generateProgress.processed / generateProgress.total) * 100,
        );
        return `Projecting… (${Math.max(0, Math.min(100, pct))}%)`;
    }, [isGenerating, generateProgress]);

    return (
        <div className="panel-viewer volume-builder-3d-viewer">
            <div className="path-tool-preview">
                <div style={{ display: "flex", gap: 4, padding: "4px 8px" }}>
                    <button
                        type="button"
                        className={`zoom-button${viewMode === "preview" ? " active" : ""}`}
                        onClick={() => setViewMode("preview")}
                    >
                        3D Preview
                    </button>
                    <button
                        type="button"
                        className={`zoom-button${viewMode === "result" ? " active" : ""}`}
                        onClick={() => setViewMode("result")}
                        disabled={!generatedSvg}
                    >
                        SVG Result
                    </button>
                </div>

                <div
                    ref={mountRef}
                    style={{
                        display: viewMode === "preview" ? "block" : "none",
                        width: "100%",
                        flex: 1,
                        minHeight: 380,
                        overflow: "hidden",
                    }}
                />

                {viewMode === "result" && generatedSvg && (
                    <div className="svg-scroll-wrapper" style={{ flex: 1 }}>
                        <div
                            className="svg-container"
                            dangerouslySetInnerHTML={{ __html: generatedSvg }}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        />
                    </div>
                )}

                <div className="path-tool-footer">
                    <p className="path-tool-meta">
                        {progressText ??
                            error ??
                            (canExport
                                ? "SVG ready"
                                : "Author paths, then Generate SVG")}
                    </p>
                    <div style={{ display: "flex", gap: 6 }}>
                        <button
                            type="button"
                            className="generic-button"
                            onClick={generate}
                            disabled={isGenerating}
                        >
                            {isGenerating ? "Generating…" : "Generate SVG"}
                        </button>
                        <button
                            type="button"
                            className="generic-button"
                            onClick={handleDownload}
                            disabled={!canExport}
                        >
                            Download SVG
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
